import { Link } from '@tanstack/react-router';
import { Home, RefreshCw, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface RouteErrorProps {
  error: Error;
  reset?: () => void;
}

export function RouteError({ error, reset }: RouteErrorProps) {
  const message = error?.message || 'An unexpected error occurred';
  const stack = error?.stack;

  return (
    <div className="relative flex min-h-screen w-full flex-col items-center justify-center overflow-hidden bg-background">
      <div
        className={cn(
          'absolute inset-0',
          '[background-size:40px_40px]',
          '[background-image:linear-gradient(to_right,hsl(var(--border)/0.3)_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border)/0.3)_1px,transparent_1px)]'
        )}
      />

      <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-background [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)]" />

      <div className="relative z-10 flex flex-col items-center justify-center gap-6 px-4 text-center max-w-2xl">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
          <AlertTriangle className="h-10 w-10 text-destructive" />
        </div>

        <div className="space-y-3">
          <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
            Something went wrong
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg break-words">
            {message}
          </p>
        </div>

        {import.meta.env.DEV && stack && (
          <pre className="w-full max-w-xl overflow-auto rounded-md border bg-muted p-4 text-left text-xs text-muted-foreground">
            {stack}
          </pre>
        )}

        <div className="flex flex-col sm:flex-row items-center gap-3 mt-2">
          {reset && (
            <Button size="lg" className="gap-2" onClick={reset}>
              <RefreshCw className="h-4 w-4" />
              Try Again
            </Button>
          )}
          <Button asChild variant="outline" size="lg" className="gap-2">
            <Link to="/">
              <Home className="h-4 w-4" />
              Go to Home
            </Link>
          </Button>
          <Button
            variant="ghost"
            size="lg"
            className="gap-2"
            onClick={() => window.location.reload()}
          >
            <RefreshCw className="h-4 w-4" />
            Reload
          </Button>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
    </div>
  );
}
